import { Box, Grid, Flex, Text, Link } from '@chakra-ui/react';
import React from 'react';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { copy } from 'lib/html-entities';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';
import getApiVersionUrl from './utils/getApiVersionUrl';
import getQNetworkLinks from './utils/getQNetworkLinks';

const MAX_LINKS_COLUMNS = 3;

const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${ config.UI.footer.frontendVersion }`;
const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${ config.UI.footer.frontendCommit }`;

const Footer = () => {

  const { data: backendVersionData } = useApiQuery('config_backend_version', {
    queryOptions: {
      staleTime: Infinity,
    },
  });
  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const networkLinks = getQNetworkLinks();

  const BLOCKSCOUT_LINKS = [
    {
      icon: 'globe' as const,
      iconSize: '24px',
      text: 'qgov.io',
      url: 'https://qgov.io/',
    },
    {
      icon: 'globe' as const,
      iconSize: '24px',
      text: 'Your HQ',
      url: networkLinks?.hq ?? 'https://hq.qgov.io/',
    },
    {
      icon: 'social/twitter' as const,
      iconSize: '18px',
      text: 'Twitter',
      url: 'https://twitter.com/Qblockchain',
    },
    {
      icon: 'social/telegram_filled' as const,
      iconSize: '20px',
      text: 'Telegram',
      url: 'https://t.me/Q_Community',
    },
    {
      icon: 'social/discord' as const,
      iconSize: '24px',
      text: 'Discord',
      url: 'https://discord.com/invite/YTgkvJvZGD',
    },
  ];

  const frontendLink = (() => {
    if (config.UI.footer.frontendVersion) {
      return <Link href={ FRONT_VERSION_URL } target="_blank">{ config.UI.footer.frontendVersion }</Link>;
    }

    if (config.UI.footer.frontendCommit) {
      return <Link href={ FRONT_COMMIT_URL } target="_blank">{ config.UI.footer.frontendCommit }</Link>;
    }

    return null;
  })();

  return (
    <Box as="footer" borderTopWidth="1px" borderTopColor="solid">
      <Grid
        px={{ base: 4, lg: 12, '2xl': 6 }}
        py={{ base: 4, lg: 8 }}
        gridTemplateColumns={{ base: '1fr', lg: 'minmax(auto, 470px) 1fr' }}
        columnGap={{ lg: '32px', xl: '100px' }}
        maxW={ CONTENT_MAX_WIDTH }
        m="0 auto"
      >
        <div>
          <Flex flexWrap="wrap" columnGap={ 8 } rowGap={ 6 } mb={{ base: 5, lg: 10 }}>
            <IntTxsIndexingStatus/>
            <NetworkAddToWallet/>
          </Flex>
          <Text fontSize="xs">
            QGOV Explorer is a tool for inspecting and analyzing QGOV blockchain.<br/>
            Powered by <Link href="https://blockscout.com" target="_blank">Blockscout</Link>
          </Text>
          <Box mt={ 6 } fontSize="xs" lineHeight={ 5 }>
            { apiVersionUrl && (
              <Text>
                Backend: <Link href={ apiVersionUrl } target="_blank">{ backendVersionData?.backend_version }</Link>
              </Text>
            ) }
            { frontendLink && (
              <Text>
                Frontend: { frontendLink }
              </Text>
            ) }
            <Text>Copyright { copy } Blockscout Limited 2023-{ (new Date()).getFullYear() }</Text>
          </Box>
        </div>
        <Grid
          gap={{ base: 6, lg: 8, xl: 12 }}
          gridTemplateColumns={{
            base: 'repeat(auto-fill, 160px)',
            lg: `repeat(${ MAX_LINKS_COLUMNS }, 135px)`,
            xl: `repeat(${ MAX_LINKS_COLUMNS }, 160px)`,
          }}
          justifyContent={{ lg: 'flex-end' }}
        >
          { BLOCKSCOUT_LINKS.map(link => <FooterLinkItem { ...link } key={ link.text }/>) }
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
